import React, { useMemo } from 'react';
import useBgPages from '../../../hooks/use-bg-pages.hook';
import { useRealtimeWS } from '../../../hooks/use-realtime-ws.hook';
import { getDashboardWsUrl } from '../../../services/reports.service';
import Asaid from '../../../components/layout/asaid.component';
import Header from '../../../components/layout/header.component';
import Footer from '../../../components/layout/footer.component';
import Icon from '../../../components/forms/icon.component';
import type { MetricsWindow, RawEventPayload } from '../../../services/realtime.types';
import { pctRate } from '../../../components/utils/format';
import {
  ActiveSessionsChart,
  ByOperatorChart,
  HangupCausesChart,
  CPD_COLORS,
  fmtTime
} from './dashboard.charts';
import HistoricalSection from './historical.section';

const WS_URL = getDashboardWsUrl();

const STATE_LABEL: Record<string, { text: string; badge: string }> = {
  idle: { text: 'Inactivo', badge: 'badge-light' },
  connecting: { text: 'Conectando…', badge: 'badge-light-warning' },
  open: { text: 'En vivo', badge: 'badge-light-success' },
  closing: { text: 'Cerrando…', badge: 'badge-light' },
  closed: { text: 'Desconectado', badge: 'badge-light-danger' },
  error: { text: 'Error', badge: 'badge-light-danger' }
};

const EVENT_BADGE: Record<RawEventPayload['eventType'], string> = {
  channel_create: 'badge-light-primary',
  channel_answer: 'badge-light-info',
  channel_hangup: 'badge-light-warning',
  cpd_result: 'badge-light-dark'
};

const VERDICT_COLOR: Record<string, string> = {
  human: CPD_COLORS.human,
  voicemail: CPD_COLORS.voicemail,
  unknown: CPD_COLORS.unknown
};

// voicemailRate/humanRate del tick vienen como fraccion 0..1 -> pctRate.

function KpiCard({
  label,
  value,
  sub,
  color = 'primary',
  icon
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  color?: string;
  icon: string;
}) {
  return (
    <div className="col-6 col-md-4 col-xl">
      <div className="card shadow-sm border-0 rounded-4 h-100">
        <div className="card-body p-5 d-flex align-items-center gap-4">
          <div
            className={`d-flex align-items-center justify-content-center rounded-3 bg-light-${color} flex-shrink-0`}
            style={{ width: 46, height: 46 }}
          >
            <Icon icon={icon} size={`fs-1 text-${color}`} />
          </div>
          <div className="min-w-0">
            <div className="fs-8 text-muted fw-semibold text-uppercase text-truncate">{label}</div>
            <div className={`fs-2 fw-bolder text-${color} lh-1 mt-1`}>{value}</div>
            {sub && <div className="fs-8 text-muted mt-1">{sub}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function WindowRow({ label, w }: { label: string; w?: MetricsWindow }) {
  return (
    <tr>
      <td className="fw-bold text-gray-700">{label}</td>
      <td className="text-end">{w?.newCalls ?? '—'}</td>
      <td className="text-end">{w?.answered ?? '—'}</td>
      <td className="text-end">{w?.hangups ?? '—'}</td>
      <td className="text-end" style={{ color: CPD_COLORS.human }}>
        {w?.human ?? '—'}
      </td>
      <td className="text-end" style={{ color: CPD_COLORS.voicemail }}>
        {w?.voicemail ?? '—'}
      </td>
      <td className="text-end text-muted">{w?.unknownVerdict ?? '—'}</td>
      <td className="text-end" style={{ color: CPD_COLORS.bypassed }}>
        {w?.bypassed ?? '—'}
      </td>
      <td className="text-end">{w ? w.callsPerSec.toFixed(2) : '—'}</td>
      <td className="text-end fw-bold">{pctRate(w?.voicemailRate)}</td>
    </tr>
  );
}

const DashboardPage: React.FC = () => {
  useBgPages();
  const ws = useRealtimeWS({ url: WS_URL, maxTickHistory: 300, maxRecentEvents: 60 });
  const tick = ws.latestTick;
  const w60 = tick?.windows.last60s;
  const w5m = tick?.windows.last5m;
  const st = STATE_LABEL[ws.state] ?? STATE_LABEL.idle;

  const byOperator = useMemo(() => tick?.byOperator ?? [], [tick]);
  const byHangup = useMemo(() => tick?.byHangupCause ?? [], [tick]);

  return (
    <div className="page d-flex flex-row flex-column-fluid">
      <Asaid />
      <div className="wrapper d-flex flex-column flex-row-fluid" id="kt_wrapper">
        <Header />
        <div
          className="content fs-6 d-flex flex-column flex-column-fluid bg-cards-p"
          id="kt_content"
        >
          <div className="post fs-6 d-flex flex-column-fluid" id="kt_post">
            <div className="container-fluid px-4 px-md-10 px-xxl-15">
              {/* Barra de estado */}
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-5">
                <div>
                  <h1 className="fw-bolder fs-2x mb-1">Dashboard CPD</h1>
                  <div className="text-muted fs-7">
                    Tiempo real desde los FS (ESL → Kafka → WS) · tick cada{' '}
                    {ws.hello?.tickMs ?? 1000} ms
                    {tick && <> · último {fmtTime(tick.ts)}</>}
                  </div>
                </div>
                <div className="d-flex align-items-center gap-3">
                  <span className={`badge ${st.badge} fs-7 fw-bold py-2 px-4`}>
                    {ws.state === 'open' && (
                      <span className="bullet bullet-dot bg-success h-8px w-8px me-2 animation-blink" />
                    )}
                    {st.text}
                    {ws.lastCloseCode && ws.state !== 'open' && (
                      <span className="ms-2 text-muted fw-normal">(close {ws.lastCloseCode})</span>
                    )}
                  </span>
                  {ws.state !== 'open' && ws.state !== 'connecting' && (
                    <button
                      type="button"
                      className="btn btn-sm btn-light-primary fw-bold"
                      onClick={ws.reconnect}
                    >
                      <Icon icon="ki-arrows-circle" size="fs-4 me-1" /> Reconectar
                    </button>
                  )}
                </div>
              </div>

              {ws.lastCloseCode === 4403 && (
                <div className="alert alert-danger d-flex align-items-center p-4 mb-5">
                  <Icon icon="ki-shield-cross" size="fs-2 me-3" />
                  Tu rol no tiene acceso al canal de tiempo real (close 4403).
                </div>
              )}
              {ws.lastError && (
                <div className="alert alert-warning d-flex align-items-center p-4 mb-5">
                  <Icon icon="ki-information-5" size="fs-2 me-3" />
                  {ws.lastError.code}: {ws.lastError.message}
                </div>
              )}

              {/* KPIs */}
              <div className="row g-4">
                <KpiCard
                  label="Sesiones activas"
                  value={tick?.activeSessions ?? '—'}
                  sub="channel_create − channel_hangup"
                  color="primary"
                  icon="ki-phone"
                />
                <KpiCard
                  label="Llamadas / seg"
                  value={w60 ? w60.callsPerSec.toFixed(2) : '—'}
                  sub={w60 ? `${w60.newCalls} nuevas en 60 s` : 'últimos 60 s'}
                  color="info"
                  icon="ki-rocket"
                />
                <KpiCard
                  label="Buzón detectado (5 m)"
                  value={pctRate(w5m?.voicemailRate)}
                  sub={w5m ? `${w5m.voicemail} cortadas con 603` : 'últimos 5 min'}
                  color="danger"
                  icon="ki-voice"
                />
                <KpiCard
                  label="Humano (5 m)"
                  value={pctRate(w5m?.humanRate)}
                  sub={w5m ? `${w5m.human} contestadas por persona` : 'últimos 5 min'}
                  color="success"
                  icon="ki-user"
                />
                <KpiCard
                  label="Bypass (5 m)"
                  value={w5m?.bypassed ?? '—'}
                  sub="rutas sin CPD (onering)"
                  color="dark"
                  icon="ki-arrow-right"
                />
              </div>

              {/* Series */}
              <div className="row g-6 mt-2">
                <div className="col-xl-7">
                  <div className="card shadow-sm border-0 rounded-4 h-100">
                    <div className="card-header border-0 pt-5">
                      <h3 className="card-title fw-bolder fs-4">
                        Actividad (últimos {Math.round(ws.tickHistory.length)} s)
                      </h3>
                    </div>
                    <div className="card-body pt-0">
                      {ws.tickHistory.length > 1 ? (
                        <ActiveSessionsChart ticks={ws.tickHistory} />
                      ) : (
                        <div className="text-muted text-center py-15">
                          Esperando ticks del servidor…
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-xl-5">
                  <div className="card shadow-sm border-0 rounded-4 h-100">
                    <div className="card-header border-0 pt-5">
                      <h3 className="card-title fw-bolder fs-4">Veredicto por operador (5 m)</h3>
                    </div>
                    <div className="card-body pt-0">
                      {byOperator.length ? (
                        <ByOperatorChart rows={byOperator} />
                      ) : (
                        <div className="text-muted text-center py-15">
                          Sin veredictos en los últimos 5 min
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Ventanas + hangup causes + feed */}
              <div className="row g-6 mt-2">
                <div className="col-xl-7">
                  <div className="card shadow-sm border-0 rounded-4">
                    <div className="card-header border-0 pt-5">
                      <h3 className="card-title fw-bolder fs-4">Ventanas móviles</h3>
                    </div>
                    <div className="card-body pt-0 table-responsive">
                      <table className="table table-row-dashed table-sm fs-7 align-middle mb-0">
                        <thead>
                          <tr className="text-muted fw-bold">
                            <th>Ventana</th>
                            <th className="text-end">Nuevas</th>
                            <th className="text-end">Contest.</th>
                            <th className="text-end">Colgadas</th>
                            <th className="text-end">Humano</th>
                            <th className="text-end">Buzón</th>
                            <th className="text-end">Sin ver.</th>
                            <th className="text-end">Bypass</th>
                            <th className="text-end">Llam/s</th>
                            <th className="text-end">% buzón</th>
                          </tr>
                        </thead>
                        <tbody>
                          <WindowRow label="10 s" w={tick?.windows.last10s} />
                          <WindowRow label="60 s" w={tick?.windows.last60s} />
                          <WindowRow label="5 min" w={tick?.windows.last5m} />
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="card shadow-sm border-0 rounded-4 mt-6">
                    <div className="card-header border-0 pt-5">
                      <h3 className="card-title fw-bolder fs-4">Hangup causes (5 m)</h3>
                    </div>
                    <div className="card-body pt-0">
                      {byHangup.length ? (
                        <HangupCausesChart rows={byHangup} height={220} />
                      ) : (
                        <div className="text-muted text-center py-10">
                          Sin cuelgues en los últimos 5 min
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-xl-5">
                  <div className="card shadow-sm border-0 rounded-4 h-100">
                    <div className="card-header border-0 pt-5 d-flex align-items-center justify-content-between">
                      <h3 className="card-title fw-bolder fs-4 mb-0">Eventos en vivo</h3>
                      <span className="badge badge-light fs-8">{ws.recentEvents.length}</span>
                    </div>
                    <div className="card-body pt-0" style={{ maxHeight: 560, overflowY: 'auto' }}>
                      {ws.recentEvents.length === 0 && (
                        <div className="text-muted text-center py-10">Sin eventos todavía</div>
                      )}
                      {ws.recentEvents.map((e, i) => (
                        <div
                          key={`${e.callUuid}-${e.eventType}-${e.ts}-${i}`}
                          className="d-flex align-items-start gap-3 py-2 border-bottom border-gray-200"
                        >
                          <span
                            className="text-muted fs-8 font-monospace flex-shrink-0"
                            style={{ width: 62 }}
                          >
                            {fmtTime(e.ts)}
                          </span>
                          <span
                            className={`badge ${EVENT_BADGE[e.eventType] ?? 'badge-light'} fs-9 flex-shrink-0`}
                          >
                            {e.eventType.replace('channel_', '')}
                          </span>
                          <div className="min-w-0 fs-8 flex-grow-1">
                            <span className="fw-bold text-uppercase">{e.operatorId || '—'}</span>
                            {e.routeGroupId !== undefined && (
                              <span className="text-muted"> · grupo {e.routeGroupId}</span>
                            )}
                            <span className="text-muted"> · {e.fsNode}</span>
                            {e.cpdVerdict && (
                              <span
                                className="fw-bold ms-2"
                                style={{ color: VERDICT_COLOR[e.cpdVerdict] }}
                              >
                                {e.cpdVerdict}
                              </span>
                            )}
                            {e.hangupCause && (
                              <span className="text-muted ms-2">{e.hangupCause}</span>
                            )}
                            <div
                              className="text-muted font-monospace text-truncate"
                              title={e.callUuid}
                            >
                              {e.callUuid}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <HistoricalSection />
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default DashboardPage;
